import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Toolbar,
  Tooltip,
  IconButton,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  FilterListOff,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ReservationStatusChip } from '../components/ReservationStatusChip';
import { formatDate, formatCurrency } from '../utils/formatters';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../utils/runtimeUrls';
import { TableFilterCell } from '../components/TableFilterCell';

interface Reservation {
  id: number;
  reservationNumber: string;
  vehicle: any;
  driver?: any;
  customer?: any;
  company: any;
  startDate: string;
  endDate: string;
  totalDays: number;
  dailyRate: number;
  totalAmount: number;
  status: string;
  pickupLocation: string;
  returnLocation: string;
  specialRequests: string;
  createdAt: string;
  guestDriverName?: string;
}

// API functions
const companyReservationsApi = {
  getCompanyReservations: async (companyId: number) => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_BASE_URL}/companies/${companyId}/reservations`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  confirmReservation: async (reservationId: number) => {
    const token = localStorage.getItem('token');
    const response = await axios.patch(
      `${API_BASE_URL}/reservations/${reservationId}/confirm`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
  declineReservation: async ({
    reservationId,
    declineReason,
  }: {
    reservationId: number;
    declineReason?: string;
  }) => {
    const token = localStorage.getItem('token');
    const response = await axios.patch(
      `${API_BASE_URL}/reservations/${reservationId}/decline`,
      null,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: declineReason?.trim() ? { declineReason: declineReason.trim() } : undefined,
      }
    );
    return response.data;
  },
};

export const CompanyReservationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Column filters and sorting
  const [columnFilters, setColumnFilters] = useState({
    reservationNumber: '',
    customerFirstName: '',
    customerLastName: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    startDate: '',
    endDate: '',
    duration: '',
    amount: '',
    status: '',
  });
  
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const companyId = user?.company?.id;

  // Fetch company reservations
  const { data: reservations, isLoading, error } = useQuery(
    ['companyReservations', companyId],
    () => companyReservationsApi.getCompanyReservations(companyId!),
    { enabled: !!companyId }
  );

  // Confirm reservation mutation
  const confirmMutation = useMutation(companyReservationsApi.confirmReservation, {
    onSuccess: () => {
      queryClient.invalidateQueries('companyReservations');
      setConfirmDialogOpen(false);
      setSelectedReservation(null);
    },
  });

  const declineMutation = useMutation(companyReservationsApi.declineReservation, {
    onSuccess: () => {
      queryClient.invalidateQueries('companyReservations');
      setDeclineDialogOpen(false);
      setSelectedReservation(null);
      setDeclineReason('');
    },
  });

  const handleConfirmClick = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setConfirmDialogOpen(true);
  };

  const handleDeclineClick = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setDeclineReason('');
    setDeclineDialogOpen(true);
  };

  const handleConfirm = () => {
    if (selectedReservation) {
      confirmMutation.mutate(selectedReservation.id);
    }
  };

  const handleDecline = () => {
    if (selectedReservation) {
      declineMutation.mutate({
        reservationId: selectedReservation.id,
        declineReason,
      });
    }
  };

  const filterReservations = useCallback((reservations: Reservation[]) => {
    if (!reservations) return [];
    let filtered = reservations;
    
    // Tab filtering
    switch (selectedTab) {
      case 0: // All
        filtered = reservations;
        break;
      case 1: // Pending
        filtered = reservations.filter(r => r.status === 'PENDING');
        break;
      case 2: // Confirmed
        filtered = reservations.filter(r => r.status === 'CONFIRMED');
        break;
      case 3: // In Progress
        filtered = reservations.filter(r => r.status === 'IN_PROGRESS');
        break;
      case 4: // Completed
        filtered = reservations.filter(r => r.status === 'COMPLETED');
        break;
      default:
        filtered = reservations;
    }
    
    // Column filtering
    filtered = filtered.filter((reservation) => {
      const matchesReservationNumber = !columnFilters.reservationNumber || 
        reservation.reservationNumber?.toLowerCase().includes(columnFilters.reservationNumber.toLowerCase());
      const matchesFirstName = !columnFilters.customerFirstName || 
        reservation.driver?.firstName?.toLowerCase().includes(columnFilters.customerFirstName.toLowerCase());
      const matchesLastName = !columnFilters.customerLastName || 
        reservation.driver?.lastName?.toLowerCase().includes(columnFilters.customerLastName.toLowerCase());
      const matchesMake = !columnFilters.vehicleMake || 
        reservation.vehicle?.make?.toLowerCase().includes(columnFilters.vehicleMake.toLowerCase());
      const matchesModel = !columnFilters.vehicleModel || 
        reservation.vehicle?.model?.toLowerCase().includes(columnFilters.vehicleModel.toLowerCase());
      const matchesYear = !columnFilters.vehicleYear || 
        String(reservation.vehicle?.year || '').includes(columnFilters.vehicleYear);
      const matchesStartDate = !columnFilters.startDate || 
        formatDate(reservation.startDate).toLowerCase().includes(columnFilters.startDate.toLowerCase());
      const matchesEndDate = !columnFilters.endDate || 
        formatDate(reservation.endDate).toLowerCase().includes(columnFilters.endDate.toLowerCase());
      const matchesDuration = !columnFilters.duration || 
        String(reservation.totalDays || '').includes(columnFilters.duration);
      const matchesAmount = !columnFilters.amount || 
        formatCurrency(reservation.totalAmount).toLowerCase().includes(columnFilters.amount.toLowerCase());
      const matchesStatus = !columnFilters.status || 
        reservation.status?.toLowerCase().includes(columnFilters.status.toLowerCase());
      
      return matchesReservationNumber && matchesFirstName && matchesLastName && 
             matchesMake && matchesModel && matchesYear && matchesStartDate && 
             matchesEndDate && matchesDuration && matchesAmount && matchesStatus;
    });
    
    // Sorting
    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        switch (sortConfig.key) {
          case 'reservationNumber':
            aValue = a.reservationNumber || '';
            bValue = b.reservationNumber || '';
            break;
          case 'customerFirstName':
            aValue = a.driver?.firstName || '';
            bValue = b.driver?.firstName || '';
            break;
          case 'customerLastName':
            aValue = a.driver?.lastName || '';
            bValue = b.driver?.lastName || '';
            break;
          case 'vehicleMake':
            aValue = a.vehicle?.make || '';
            bValue = b.vehicle?.make || '';
            break;
          case 'vehicleModel':
            aValue = a.vehicle?.model || '';
            bValue = b.vehicle?.model || '';
            break;
          case 'vehicleYear':
            aValue = a.vehicle?.year || 0;
            bValue = b.vehicle?.year || 0;
            break;
          case 'startDate':
            aValue = new Date(a.startDate).getTime();
            bValue = new Date(b.startDate).getTime();
            break;
          case 'endDate':
            aValue = new Date(a.endDate).getTime();
            bValue = new Date(b.endDate).getTime();
            break;
          case 'duration':
            aValue = a.totalDays || 0;
            bValue = b.totalDays || 0;
            break;
          case 'amount':
            aValue = a.totalAmount || 0;
            bValue = b.totalAmount || 0;
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          default:
            return 0;
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        return sortConfig.direction === 'asc' 
          ? (aValue > bValue ? 1 : -1)
          : (bValue > aValue ? 1 : -1);
      });
    }
    
    return filtered;
  }, [selectedTab, columnFilters, sortConfig]);
  
  const handleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev?.key === key) {
        return prev.direction === 'asc' 
          ? { key, direction: 'desc' }
          : null;
      }
      return { key, direction: 'asc' };
    });
  };
  
  const handleClearFilters = () => {
    setColumnFilters({
      reservationNumber: '',
      customerFirstName: '',
      customerLastName: '',
      vehicleMake: '',
      vehicleModel: '',
      vehicleYear: '',
      startDate: '',
      endDate: '',
      duration: '',
      amount: '',
      status: '',
    });
    setSortConfig(null);
  };

  // Compute filtered reservations and pagination (must be before early returns)
  const filteredReservations = useMemo(
    () => filterReservations(reservations || []),
    [reservations, filterReservations]
  );
  const pendingCount = useMemo(() => reservations?.filter((r: Reservation) => r.status === 'PENDING').length || 0, [reservations]);
  
  const paginatedReservations = useMemo(() => {
    return filteredReservations.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredReservations, page, rowsPerPage]);

  if (!companyId) {
    return (
      <Container maxWidth="lg" sx={{ py: 1, px: 1 }}>
        <Alert severity="error">{t('companyReservations.noCompany')}</Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 1, px: 1 }}>
        <Alert severity="error">{t('companyReservations.loadFailed')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 1, px: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4" component="h1">
          {t('companyReservations.title')}
        </Typography>
        {pendingCount > 0 && (
          <Chip label={t('companyReservations.pendingApprovals', { count: pendingCount })} color="warning" />
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => {
          setSelectedTab(newValue);
          setPage(0);
        }}>
          <Tab label={t('companyReservations.allTab', { count: reservations?.length || 0 })} />
          <Tab label={t('companyReservations.pendingTab', { count: pendingCount })} />
          <Tab label={t('status.confirmed')} />
          <Tab label={t('status.inProgress')} />
          <Tab label={t('status.completed')} />
        </Tabs>
      </Box>

      {/* Reservations Table */}
      {filteredReservations.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {t('reservationsPage.empty')}
          </Typography>
        </Box>
      ) : (
        <Paper elevation={1} sx={{ p: 1 }}>
          {/* Toolbar - Single Line Logistiqo Style */}
          <Toolbar sx={{ 
            pl: { sm: 1 }, 
            pr: { xs: 0.5, sm: 1 }, 
            minHeight: '48px !important', 
            py: 0.5, 
            bgcolor: 'background.paper', 
            borderBottom: '1px solid', 
            borderColor: 'divider' 
          }}>
            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Pagination Controls - Center */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => setPage(prev => Math.max(0, prev - 1))}
                disabled={page === 0}
                sx={{ 
                  width: '28px',
                  height: '28px',
                  '&.Mui-disabled': { opacity: 0.3 }
                }}
              >
                <ArrowUpward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
              </IconButton>
              <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', minWidth: '80px', textAlign: 'center' }}>
                {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredReservations.length)} {t('common.of')} {filteredReservations.length}
              </Typography>
              
              {/* Page Numbers */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {Array.from({ length: Math.min(5, Math.ceil(filteredReservations.length / rowsPerPage)) }, (_, i) => {
                  const totalPages = Math.ceil(filteredReservations.length / rowsPerPage);
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (page < 2) {
                    pageNum = i;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  if (pageNum < 0 || pageNum >= totalPages) return null;

                  return (
                    <Button
                      key={pageNum}
                      size="small"
                      onClick={() => setPage(pageNum)}
                      sx={{
                        minWidth: '32px',
                        height: '28px',
                        px: 1,
                        fontSize: '0.8125rem',
                        bgcolor: page === pageNum ? 'primary.main' : 'transparent',
                        color: page === pageNum ? 'white' : 'text.primary',
                        '&:hover': {
                          bgcolor: page === pageNum ? 'primary.dark' : 'action.hover'
                        }
                      }}
                    >
                      {pageNum + 1}
                    </Button>
                  );
                })}
                {Math.ceil(filteredReservations.length / rowsPerPage) > 5 && page < Math.ceil(filteredReservations.length / rowsPerPage) - 3 && (
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', px: 0.5 }}>
                    ...
                  </Typography>
                )}
                {Math.ceil(filteredReservations.length / rowsPerPage) > 5 && page < Math.ceil(filteredReservations.length / rowsPerPage) - 3 && (
                  <Button
                    size="small"
                    onClick={() => setPage(Math.ceil(filteredReservations.length / rowsPerPage) - 1)}
                    sx={{
                      minWidth: '32px',
                      height: '28px',
                      px: 1,
                      fontSize: '0.8125rem',
                    }}
                  >
                    {Math.ceil(filteredReservations.length / rowsPerPage)}
                  </Button>
                )}
              </Box>

              <IconButton
                size="small"
                onClick={() => setPage(prev => Math.min(Math.ceil(filteredReservations.length / rowsPerPage) - 1, prev + 1))}
                disabled={page >= Math.ceil(filteredReservations.length / rowsPerPage) - 1}
                sx={{ 
                  width: '28px',
                  height: '28px',
                  '&.Mui-disabled': { opacity: 0.3 }
                }}
              >
                <ArrowDownward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
              </IconButton>
              
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                size="small"
                sx={{ 
                  height: '28px',
                  fontSize: '0.8125rem',
                  ml: 1,
                  '& .MuiSelect-select': { py: 0.5, px: 1 }
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </Box>

            {/* Clear Filters Button - Right */}
            <Tooltip title={t('common.clearFilters')}>
              <IconButton
                onClick={handleClearFilters}
                size="small"
                sx={{ 
                  width: '32px', 
                  height: '32px',
                  ml: 1
                }}
              >
                <FilterListOff fontSize="small" />
              </IconButton>
            </Tooltip>
          </Toolbar>

          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableFilterCell placeholder={t('fields.reservationNumber')} value={columnFilters.reservationNumber} onChange={(value) => setColumnFilters((prev) => ({ ...prev, reservationNumber: value }))} onSort={() => handleSort('reservationNumber')} sortDirection={sortConfig?.key === 'reservationNumber' ? sortConfig.direction : null} width="130px" cellSx={{ py: 0.5, px: 1 }} textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { py: 0 } }} />
                  <TableFilterCell placeholder={t('fields.firstName')} value={columnFilters.customerFirstName} onChange={(value) => setColumnFilters((prev) => ({ ...prev, customerFirstName: value }))} onSort={() => handleSort('customerFirstName')} sortDirection={sortConfig?.key === 'customerFirstName' ? sortConfig.direction : null} width="120px" cellSx={{ py: 0.5, px: 1 }} textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { py: 0 } }} />
                  <TableFilterCell placeholder={t('fields.lastName')} value={columnFilters.customerLastName} onChange={(value) => setColumnFilters((prev) => ({ ...prev, customerLastName: value }))} onSort={() => handleSort('customerLastName')} sortDirection={sortConfig?.key === 'customerLastName' ? sortConfig.direction : null} width="120px" cellSx={{ py: 0.5, px: 1 }} textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { py: 0 } }} />
                  <TableFilterCell placeholder={t('fields.make')} value={columnFilters.vehicleMake} onChange={(value) => setColumnFilters((prev) => ({ ...prev, vehicleMake: value }))} onSort={() => handleSort('vehicleMake')} sortDirection={sortConfig?.key === 'vehicleMake' ? sortConfig.direction : null} width="120px" cellSx={{ py: 0.5, px: 1 }} textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { py: 0 } }} />
                  <TableFilterCell placeholder={t('fields.model')} value={columnFilters.vehicleModel} onChange={(value) => setColumnFilters((prev) => ({ ...prev, vehicleModel: value }))} onSort={() => handleSort('vehicleModel')} sortDirection={sortConfig?.key === 'vehicleModel' ? sortConfig.direction : null} width="120px" cellSx={{ py: 0.5, px: 1 }} textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { py: 0 } }} />
                  <TableFilterCell placeholder={t('fields.year')} value={columnFilters.vehicleYear} onChange={(value) => setColumnFilters((prev) => ({ ...prev, vehicleYear: value }))} onSort={() => handleSort('vehicleYear')} sortDirection={sortConfig?.key === 'vehicleYear' ? sortConfig.direction : null} width="80px" cellSx={{ py: 0.5, px: 1 }} textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { py: 0 } }} />
                  <TableFilterCell placeholder={t('fields.startDate')} value={columnFilters.startDate} onChange={(value) => setColumnFilters((prev) => ({ ...prev, startDate: value }))} onSort={() => handleSort('startDate')} sortDirection={sortConfig?.key === 'startDate' ? sortConfig.direction : null} width="120px" cellSx={{ py: 0.5, px: 1 }} textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { py: 0 } }} />
                  <TableFilterCell placeholder={t('fields.endDate')} value={columnFilters.endDate} onChange={(value) => setColumnFilters((prev) => ({ ...prev, endDate: value }))} onSort={() => handleSort('endDate')} sortDirection={sortConfig?.key === 'endDate' ? sortConfig.direction : null} width="120px" cellSx={{ py: 0.5, px: 1 }} textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { py: 0 } }} />
                  <TableFilterCell placeholder={t('fields.duration')} value={columnFilters.duration} onChange={(value) => setColumnFilters((prev) => ({ ...prev, duration: value }))} onSort={() => handleSort('duration')} sortDirection={sortConfig?.key === 'duration' ? sortConfig.direction : null} width="100px" cellSx={{ py: 0.5, px: 1 }} textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { py: 0 } }} />
                  <TableFilterCell placeholder={t('fields.amount')} value={columnFilters.amount} onChange={(value) => setColumnFilters((prev) => ({ ...prev, amount: value }))} onSort={() => handleSort('amount')} sortDirection={sortConfig?.key === 'amount' ? sortConfig.direction : null} width="100px" cellSx={{ py: 0.5, px: 1 }} textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { py: 0 } }} />
                  <TableFilterCell placeholder={t('fields.status')} value={columnFilters.status} onChange={(value) => setColumnFilters((prev) => ({ ...prev, status: value }))} onSort={() => handleSort('status')} sortDirection={sortConfig?.key === 'status' ? sortConfig.direction : null} width="120px" cellSx={{ py: 0.5, px: 1 }} textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { py: 0 } }} />
                  <TableCell align="right" sx={{ py: 0.5, px: 1, width: '180px', maxWidth: '180px', verticalAlign: 'bottom' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{t('fields.actions')}</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedReservations.map((reservation: Reservation) => (
                  <TableRow key={reservation.id} hover>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8125rem' }}>
                          {reservation.reservationNumber || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                          {reservation.driver?.firstName || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                          {reservation.driver?.lastName || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                          {reservation.vehicle?.make || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                          {reservation.vehicle?.model || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                          {reservation.vehicle?.year || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                          {formatDate(reservation.startDate)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                          {reservation.endDate ? formatDate(reservation.endDate) : t('rental.returnDateTbd')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                          {t('common.daysCount', { count: reservation.totalDays || 0 })}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8125rem' }}>
                          {formatCurrency(reservation.totalAmount)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <ReservationStatusChip
                        status={reservation.status}
                        sx={{
                          fontSize: '0.7rem',
                          height: '20px',
                          '& .MuiChip-label': {
                            px: 0.75,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100px'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
                      {reservation.status === 'PENDING' && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleConfirmClick(reservation)}
                            disabled={confirmMutation.isLoading || declineMutation.isLoading}
                            sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}
                          >{t('common.confirm')}</Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleDeclineClick(reservation)}
                            disabled={confirmMutation.isLoading || declineMutation.isLoading}
                            sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}
                          >{t('common.decline')}</Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>{t('companyReservations.confirmReservation')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" gutterBottom>
            {t('companyReservations.confirmBody')}
          </Typography>
          {selectedReservation && (
            <Box sx={{ my: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{t('companyReservations.reservationLabel')}</strong> {selectedReservation.reservationNumber}
              </Typography>
              <Typography variant="body2">
                <strong>{t('fields.driver', 'Driver')}:</strong> {selectedReservation.driver?.firstName} {selectedReservation.driver?.lastName}
              </Typography>
              <Typography variant="body2">
                <strong>{t('companyReservations.vehicleLabel')}</strong> {selectedReservation.vehicle?.make} {selectedReservation.vehicle?.model}
              </Typography>
              <Typography variant="body2">
                <strong>{t('companyReservations.datesLabel')}</strong> {formatDate(selectedReservation.startDate)} - {formatDate(selectedReservation.endDate)}
              </Typography>
              <Typography variant="body2">
                <strong>{t('companyReservations.amountLabel')}</strong> {formatCurrency(selectedReservation.totalAmount)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleConfirm}
            color="success"
            variant="contained"
            disabled={confirmMutation.isLoading}
          >
            {confirmMutation.isLoading ? t('common.confirming') : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={declineDialogOpen}
        onClose={() => {
          setDeclineDialogOpen(false);
          setDeclineReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>{t('companyReservations.declineReservation')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" gutterBottom>
            {t('companyReservations.declineBody')}
          </Typography>
          {selectedReservation && (
            <Box sx={{ my: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{t('companyReservations.reservationLabel')}</strong> {selectedReservation.reservationNumber}
              </Typography>
              <Typography variant="body2">
                <strong>{t('fields.driver', 'Driver')}:</strong> {selectedReservation.driver?.firstName} {selectedReservation.driver?.lastName}
              </Typography>
              <Typography variant="body2">
                <strong>{t('companyReservations.vehicleLabel')}</strong> {selectedReservation.vehicle?.make} {selectedReservation.vehicle?.model}
              </Typography>
              <Typography variant="body2">
                <strong>{t('companyReservations.datesLabel')}</strong> {formatDate(selectedReservation.startDate)} - {formatDate(selectedReservation.endDate)}
              </Typography>
              <Typography variant="body2">
                <strong>{t('companyReservations.amountLabel')}</strong> {formatCurrency(selectedReservation.totalAmount)}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('dialogs.cancellationReason')}
            value={declineReason}
            onChange={(event) => setDeclineReason(event.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeclineDialogOpen(false);
              setDeclineReason('');
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDecline}
            color="error"
            variant="contained"
            disabled={declineMutation.isLoading}
          >
            {declineMutation.isLoading ? t('common.loading') : t('common.decline')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
